'use client'

import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/utilities/ui'
import { SUB_BRANDS, SUB_BRAND_LOGO_SLOT, type SubBrand } from './constants'
import { useInView } from './Reveal'
import { useCopyToClipboard } from './useCopyToClipboard'

/** useLayoutEffect warns during SSR; fall back to useEffect on the server. */
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/** White wordmark from the solid-black tight logo - avoids needing separate white SVGs. */
const WHITE_LOGO_FILTER = 'brightness(0) invert(1)'

/** Tab display label: "AI" stays uppercase, others title-cased. */
const tabLabel = (brand: SubBrand) =>
  brand.key === 'ai' ? 'AI' : brand.suffix.charAt(0).toUpperCase() + brand.suffix.slice(1)

const SubBrandCard: React.FC<{ brand: SubBrand; index: number }> = ({ brand, index }) => {
  const { ref, inView } = useInView<HTMLButtonElement>(0.25)
  const { copied, copy } = useCopyToClipboard()

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => copy(brand.hex)}
      aria-label={`Copy Civo ${brand.suffix} hex value ${brand.hex}`}
      style={{ transitionDelay: inView ? `${index * 90}ms` : '0ms' }}
      className={cn(
        'group flex w-full flex-col overflow-hidden rounded-[16px] border border-civo-cloud bg-white text-left',
        'transition-[opacity,transform,box-shadow] duration-500 ease-out',
        'hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(0,0,0,0.4)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-black/40',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
        inView ? 'opacity-100' : 'translate-y-4 opacity-0',
      )}
    >
      {/* Colour block wipes down to fill on reveal */}
      <span
        aria-hidden="true"
        className={cn(
          'relative block h-[120px] origin-top overflow-hidden transition-transform duration-700 ease-out',
          'motion-reduce:transition-none motion-reduce:scale-y-100',
          inView ? 'scale-y-100' : 'scale-y-0',
        )}
        style={{ backgroundColor: brand.bg, transitionDelay: inView ? `${index * 90 + 120}ms` : '0ms' }}
      >
        {/* Glossy sheen sweeps across once on hover (desktop grid only) */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 w-1/3',
            '-translate-x-[150%] -skew-x-12 bg-gradient-to-r from-transparent via-white/40 to-transparent',
            'group-hover:animate-sheen motion-reduce:hidden',
          )}
        />
      </span>

      <div className="flex flex-1 flex-col gap-4 p-7">
        {/* Fixed-height slot keeps every card's content aligned regardless of per-logo height */}
        <div className="flex items-start" style={{ height: SUB_BRAND_LOGO_SLOT }}>
          <Image
            src={`/assets-rebrand/logos/civo-${brand.key}-black.svg`}
            alt={`Civo ${brand.suffix}`}
            width={brand.width}
            height={brand.height}
            unoptimized
            style={{ height: brand.logoHeight }}
            className="w-auto transition-[filter] duration-300 group-hover:saturate-[1.35] motion-reduce:transition-none"
          />
        </div>
        <p className="body-copy-medium text-black/70">{brand.description}</p>

        <div className="mt-auto flex flex-col gap-2 border-t border-civo-cloud pt-4">
          <span className="subheading-small text-civo-black">{brand.hex}</span>
          <span className="body-small text-black/60">{brand.rgba}</span>
          <div className="flex items-center justify-between gap-2">
            <span className="body-small text-black/60">{brand.cmyk}</span>
            <span aria-live="polite" className="body-small text-black/50">
              {copied ? 'Copied!' : 'Click to copy'}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

/** Tablet/mobile: pill tablist + a single preview panel (Figma 2150-1176 / 2185-1448). */
const SubBrandSwitcher: React.FC = () => {
  const baseId = useId()
  const panelId = `${baseId}-panel`
  const tabId = (key: SubBrand['key']) => `${baseId}-tab-${key}`

  const [activeKey, setActiveKey] = useState<SubBrand['key']>(SUB_BRANDS[0].key)
  const active = SUB_BRANDS.find((b) => b.key === activeKey) ?? SUB_BRANDS[0]
  const { copied, copy } = useCopyToClipboard()

  // Preview logos run ~30px (vs 39px on the cards); keep ai's relative reduction.
  const previewLogoHeight = Math.round((active.logoHeight * 30) / SUB_BRAND_LOGO_SLOT)

  // Sliding indicator: tablet tabs are hug-width, so the pill's position/size can't be
  // expressed in CSS alone - measure the active tab and animate a single pill behind it.
  const tablistRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const [pill, setPill] = useState({ left: 0, top: 0, width: 0, height: 0 })
  const [pillReady, setPillReady] = useState(false)

  useIsomorphicLayoutEffect(() => {
    const measure = () => {
      const el = tabRefs.current[activeKey]
      if (!el || el.offsetWidth === 0) return // hidden at lg+ measures 0
      setPill({ left: el.offsetLeft, top: el.offsetTop, width: el.offsetWidth, height: el.offsetHeight })
      setPillReady(true)
    }
    measure()
    // ResizeObserver re-measures on breakpoint changes and on web-font swap (which reflows
    // the hug-width tabs) - cases a window resize listener alone would miss.
    const ro = new ResizeObserver(measure)
    const node = tablistRef.current
    if (node) ro.observe(node)
    return () => ro.disconnect()
  }, [activeKey])

  // Roving focus + arrow-key navigation across the tablist (WAI-ARIA tabs pattern).
  const onTabKeyDown = (e: React.KeyboardEvent) => {
    const keys = SUB_BRANDS.map((b) => b.key)
    const i = keys.indexOf(activeKey)
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % keys.length
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + keys.length) % keys.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = keys.length - 1
    if (next === null) return
    e.preventDefault()
    const nextKey = keys[next]
    setActiveKey(nextKey)
    tabRefs.current[nextKey]?.focus()
  }

  return (
    <div className="flex flex-col gap-8 lg:hidden">
      {/* Tab pills. Mobile (Compact): full-width equal tabs, short labels, no dot, 14px.
          Tablet (md+, Default): hug-width tabs with a colour dot and "Civo X" at 18px.
          The active state is a single pill that slides between tabs. */}
      <div
        ref={tablistRef}
        role="tablist"
        aria-label="Sub-brands"
        aria-orientation="horizontal"
        onKeyDown={onTabKeyDown}
        className="relative flex w-full gap-2 rounded-full border border-neutral-200 bg-white p-1.5 md:w-fit md:max-w-full"
      >
        {/* Sliding active pill */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute rounded-full transition-[transform,width,height,background-color] duration-300 ease-out',
            'motion-reduce:transition-none',
            pillReady ? 'opacity-100' : 'opacity-0',
          )}
          style={{
            transform: `translate(${pill.left}px, ${pill.top}px)`,
            width: pill.width,
            height: pill.height,
            backgroundColor: active.bg,
            left: 0,
            top: 0,
          }}
        />
        {SUB_BRANDS.map((b) => {
          const isActive = b.key === activeKey
          return (
            <button
              key={b.key}
              ref={(el) => {
                tabRefs.current[b.key] = el
              }}
              type="button"
              role="tab"
              id={tabId(b.key)}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveKey(b.key)}
              className={cn(
                'relative z-10 flex items-center justify-center whitespace-nowrap rounded-full font-sans text-sm transition-colors duration-300',
                'flex-1 px-3.5 py-2.5',
                'md:flex-none md:gap-2.5 md:px-6 md:py-3',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-black/40',
                'motion-reduce:transition-none',
                isActive ? 'text-civo-white' : 'text-civo-black',
              )}
            >
              <span
                aria-hidden="true"
                className="hidden size-3 shrink-0 rounded-full transition-colors duration-300 md:block"
                style={{ backgroundColor: isActive ? '#FFFFFF' : b.bg }}
              />
              <span className="md:hidden">{tabLabel(b)}</span>
              <span className="hidden md:inline">Civo {tabLabel(b)}</span>
            </button>
          )
        })}
      </div>

      {/* Preview panel for the active sub-brand */}
      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId(activeKey)}
        style={{ backgroundColor: active.bg }}
        className={cn(
          'relative flex w-full flex-col gap-6 overflow-hidden rounded-[20px] px-8 py-9 text-civo-white transition-colors duration-300 ease-out',
          'md:flex-row md:px-12 md:py-11',
          'motion-reduce:transition-none',
        )}
      >
        {/* Glossy sheen sweeps across when the active sub-brand changes (keyed to replay) */}
        <span
          key={activeKey}
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 z-10 w-1/3',
            '-translate-x-[150%] -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent',
            'animate-sheen motion-reduce:hidden',
          )}
        />
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex items-start" style={{ height: 30 }}>
            <Image
              src={`/assets-rebrand/logos/civo-${active.key}-black.svg`}
              alt={`Civo ${active.suffix}`}
              width={active.width}
              height={active.height}
              unoptimized
              style={{ height: previewLogoHeight, filter: WHITE_LOGO_FILTER }}
              className="w-auto"
            />
          </div>
          <p className="body-large">{active.description}</p>
        </div>

        <div className="flex shrink-0 flex-col gap-2 whitespace-nowrap">
          <button
            type="button"
            onClick={() => copy(active.hex)}
            aria-label={`Copy hex ${active.hex}`}
            className="subheading-medium w-fit rounded-[4px] text-left transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-white/60 motion-reduce:transition-none"
          >
            {active.hex}
          </button>
          <span className="body-small">{active.rgba}</span>
          <span className="body-small">{active.cmyk}</span>
          <span aria-live="polite" className="sr-only">
            {copied ? 'Copied!' : ''}
          </span>
        </div>
      </div>
    </div>
  )
}

export const SubBrandCards: React.FC = () => (
  <>
    {/* Desktop: 3-up card grid */}
    <div className="hidden gap-6 lg:grid lg:grid-cols-3">
      {SUB_BRANDS.map((brand, i) => (
        <SubBrandCard key={brand.key} brand={brand} index={i} />
      ))}
    </div>
    {/* Tablet & mobile: tab switcher */}
    <SubBrandSwitcher />
  </>
)
